/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

@JvmInline
@Serializable
value class Password(
    @SerialName("password") val plaintext: String
) {
    init {
        require(plaintext.length >= MIN_PASSWORD_LENGTH) { "Password must have minimum length" }
        require(plaintext.length <= MAX_PASSWORD_LENGTH) { "Password length must be <= $MAX_PASSWORD_LENGTH but is ${plaintext.length}" }
    }

    enum class ValidationFailure {
        TOO_SHORT,
        TOO_LONG
    }

    companion object {
        const val MIN_PASSWORD_LENGTH = 8
        const val MAX_PASSWORD_LENGTH = 50

        /**
         * @return List of validation failures for the password.  If the list is empty, then the password has passed validation.
         * Note that the server may still reject the value due to additional validations added in the future.
         */
        fun validate(password: String): Set<ValidationFailure> =
            buildSet<ValidationFailure> {
                if (password.length < MIN_PASSWORD_LENGTH) {
                    add(ValidationFailure.TOO_SHORT)
                }
                if (password.length > MAX_PASSWORD_LENGTH) {
                    add(ValidationFailure.TOO_LONG)
                }
            }
    }
}
